import React, {
  useContext,
  createContext,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { atom, useAtom } from "jotai";
import { isEmptyObject } from "../../lib/util";
import Fuse from "fuse.js";
import { IListing } from "../../lib/Listing";
import { atomWithLocation, atomWithHash } from "jotai-location";

/*
 * A filter function that can filter items from the index based on
 * a current filter value.
 *
 * @param arg an item from the search index.
 * @param value the value (eg. user selection) of the filter to be applied.
 * @return truthy if the item passes the filter, or false if the item should
 * be filtered out.
 */
export type TFilterFunc = (arg: any, value: string) => any;

/*
 * Array sort callback function that can compare items in the search index.
 */
export type TSortFunc = (arg0: any, arg1: any) => number;

export interface ISearchContext {
  index?: any[];
  currentFilter?: Map<string, TFilterFunc>;
  setFilter?: (key: string, fn: TFilterFunc) => void;
  setFilterValue?: (key: string, value: string | string[] | null) => void;
  getFilterValue?: (key: string) => string | string[] | undefined;
  currentFilterValues?: {};
  unsetFilter?: (key: string) => void;
  resetFilterValues?: () => void;
  currentSort?: Map<string, TFilterFunc>;
  setSort?: (key: string, fn: TSortFunc) => void;
  sortValue?: string | undefined;
  setSortValue?: Dispatch<SetStateAction<string>>;
  currentResults: any[];
  currentPageResults?: any[];
  setCurrentResults?: Dispatch<SetStateAction<any[]>>;
  page: number;
  setPage?: Dispatch<SetStateAction<number>>;
  perPage: number;
  setPerPage?: Dispatch<SetStateAction<number>>;
}

export const SearchContext = createContext<ISearchContext>({
  currentResults: [],
  page: 1,
  perPage: 12,
});

export interface ISearchContextProviderProps {
  index?: any[];
  children?: ReactNode;
  pageSize?: number;
  initialFilter?: string;
  initialSort?: string;
}

export const SearchContextProvider: React.FC<ISearchContextProviderProps> = ({
  index,
  children,
  pageSize = 20,
  initialFilter,
  initialSort,
}) => {
  let filterItems = {};
  let sortDefault = "";

  if (typeof window !== 'undefined') {
    let urlParams = new URLSearchParams(window?.location?.hash)
    if (urlParams.has('filters')) {
      filterItems = JSON.parse(urlParams.get('filters') || "");
    } else {
      filterItems = JSON.parse(initialFilter || "{}");
    }

    if (initialSort) {
      sortDefault = initialSort
    }
  }

  const locationAtom: any = atomWithLocation();

  const [perPageAtom, setPerPageAtom] = useState<any>(() =>
    atomWithHash("perPage", pageSize, { setHash: "replaceState" })
  );
  const [pageAtom, setPageAtom] = useState(() =>
    atom(
      (get: any) => get(locationAtom).searchParams?.get("page"),
      (get: any, set: any, name: any) => {
        set(locationAtom, (prev: any) => ({          
          ...prev,
          hash: window.location.hash,
          searchParams: new URLSearchParams([["page", name as string]])
        }));
      }
    )
  );
  const [sortAtom, setSortAtom] = useState<any>(() =>
    atomWithHash("sort", sortDefault, { setHash: "replaceState" })
  );
  const [filterValuesAtom, setFilterValuesAtom] = useState<any>(() =>
    atomWithHash("filters", filterItems as Record<string, any>, {
      setHash: "replaceState",
    })
  );
  const [currentFilter, setCurrentFilter] = useState(
    new Map<string, TFilterFunc>()
  );
  const [filterValues, setFilterValues]: any = useAtom(filterValuesAtom);
  const [currentSort, setCurrentSort] = useState(new Map<string, TSortFunc>());
  const [sortValue, setSortValue]: any = useAtom(sortAtom);
  const [currentResults, setCurrentResults] = useState(index || []);
  const [page, setPage] = useAtom(pageAtom);
  const [perPage, setPerPage]: any = useAtom(perPageAtom);

  const options = {
    includeScore: false,
    useExtendedSearch: true,
    keys: [
      { name: "fullText", getFn: (item: any) => `${item.mlsID} - ${item.address?.text ?? ""}` },
      { name: "escapedText", getFn: (item: any) => `${(item.address?.text ?? "").replace(/[^A-Za-z|^\d]/gi, "")}` },
      'mlsID',
      'address.text',
      'majorArea',
      'minorArea',
      'neighborhood',
      'subdivision',
      'address.city',
      'address.postal',
      'address.state',
      'address.street1',
      'address.street2',
      //'description',           
      //'address.country',
      //'address.county',
      //'type',
      //'subType',
    ]
  };

  const optionsBroad = {
    includeScore: false,
    threshold: 0.4,
    useExtendedSearch: true,
    keys: [
      'mlsID',
      'address.text',
      'majorArea',
      'minorArea',
      'neighborhood',
      'subdivision',
      'address.city',
      'address.postal',
      'address.state',
      'address.street1',
      'address.street2'
    ]
  };

  // Effect to apply filter values and sorts to generate updated
  // currentResults
  useEffect(() => {
    let results = [...(index || [])];
    currentFilter.forEach((fn: TFilterFunc, key: string) => {
      let val = filterValues[key];
      if (key !== "text" && val != "" && val != undefined)
        results = results.filter((arg) => {
          return fn(arg, val);
        });
    });

    if (filterValues['text']) {
      const fuse = new Fuse(results, options);
      var fuseSearchResult = fuse.search(`'"${filterValues['text']}" | '"${filterValues['text'].replace(/[^A-Za-z|^\d]/gi, "")}"`);
      if (fuseSearchResult.length > 0) {
        results = fuseSearchResult.map((i: any) => i.item);
      }
      else {
        const fuseBroad = new Fuse(results, optionsBroad);
        var fuseBroadSearchResult = fuseBroad.search(filterValues['text']);
        results = fuseBroadSearchResult.map((i: any) => i.item)
      }
    }

    sortValue &&
      currentSort.get(sortValue) &&
      results.sort(currentSort.get(sortValue));
    setCurrentResults(results);
  }, [filterValues, sortValue, currentSort, currentFilter]);
  const setFilter = (key: string, fn: TFilterFunc) => {
    let newFilter = new Map(currentFilter);
    newFilter.set(key, fn);
    setCurrentFilter(newFilter);
  };
  const unsetFilter = (key: string) => {
    if (currentFilter.get(key)) {
      let newFilter = new Map(currentFilter);
      newFilter.delete(key);
      setCurrentFilter(newFilter);
    }
  };
  const setFilterValue = (key: string, value: string | string[] | null) => {
    setPage(1);
    // Create a new object for React state changes
    let newFilterValues = {} as Record<string, any>;
    // Clone current filter values
    for (let fvk in filterValues) {
      newFilterValues[fvk] = filterValues[fvk];
    }
    if (value == null || value == "") {
      (filterValues[key] && (delete newFilterValues[key]));
      setFilterValues(newFilterValues);
    } else if (filterValues[key] != value) {
      newFilterValues[key] = value;
      setFilterValues(newFilterValues);
    }
  };
  const getFilterValue = (key: string) => {
    return filterValues[key];
  };
  const resetFilter = () => {
    setPage(1);
    setFilterValues({});
  };
  return (
    <SearchContext.Provider
      value={{
        index,
        currentFilter,
        currentFilterValues: filterValues,
        setFilter,
        unsetFilter,
        currentSort,
        setSortValue,
        sortValue,
        setCurrentResults,
        setFilterValue,
        getFilterValue,
        currentResults,
        resetFilterValues: resetFilter,
        page,
        setPage,
        perPage,
        setPerPage,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => useContext(SearchContext);
